// SidebarBuckets.jsx

import React from 'react';

// Context
import { useUI } from 'context/UIContext';

// Hooks
import useSidebarBucketsState from './hooks/useSidebarBucketsState';

// CSS
import './SidebarBuckets.css';

// Components
import SidebarBucket from './SidebarBucket';

/**
 * SidebarBuckets
 *
 * Renders base sidebar buckets and persists the open bucket by user/base.
 *
 * @param {function} onDatabaseClick - Called when a database row is selected.
 * @param {function} onRequestClose - Called when a mobile sidebar action should close the drawer.
 * @param {function} onAddDashboard - Opens the new dashboard modal.
 * @param {function} onAddChannel - Opens the new channel modal.
 * @param {function} onCreateMessage - Opens the new message / conversation modal.
 * @param {function} onAddDatabase - Opens the new database modal.
 *
 * @returns {ReactNode} The rendered sidebar buckets section.
 */
const SidebarBuckets = React.memo(({
    onDatabaseClick,
    onRequestClose,
    onAddDashboard,
    onAddChannel,
    onCreateMessage,
    onAddDatabase,
}) => {
    // ----- CONTEXT ----- //

    const { isSidebarRailCompact } = useUI();
    const compact = Boolean(isSidebarRailCompact);

    // ----- HOOKS ----- //

    const {
        bucketListState,
        handlers,
    } = useSidebarBucketsState({
        onRequestClose,
        onAddDashboard,
        onAddChannel,
        onCreateMessage,
        onAddDatabase,
    });

    // ----- RENDER ----- //

    return (
        <div
          className={[
            'sidebar-buckets-container',
            compact ? 'is-rail-compact' : '',
          ].filter(Boolean).join(' ')}
        >
            {bucketListState.buckets.map((bucket) => {
                const isOpen = compact || bucketListState.openBucketKey === bucket.key;
                const showHeader = !compact && bucket.key !== 'base';

                return (
                    <SidebarBucket
                      key={bucket.key}
                      bucket={bucket}
                      isOpen={isOpen}
                      showHeader={showHeader}
                      onAddChannel={handlers.handleAddChannel}
                      onAddDatabase={handlers.handleAddDatabase}
                      onAddFromTemplate={handlers.handleAddDashboard}
                      onChannelClick={handlers.handleChannelClick}
                      onConversationClick={handlers.handleConversationClick}
                      onCreateMessage={handlers.handleCreateMessage}
                      onDashboardClick={handlers.handleDashboardClick}
                      onDatabaseClick={onDatabaseClick}
                      onToggle={handlers.handleBucketToggle}
                    />
                );
            })}
        </div>
    );
});

// Dev Tools
SidebarBuckets.displayName = 'SidebarBuckets';

export default SidebarBuckets;
